import React, { useState, useEffect, ReactElement } from "react";
import StepHandler from "../../../../models/shared/StepHandler";
import FinancingRequestValidityRange from "../../../../models/financing-request/validity-range/FinancingRequestValidityRange";
import FinancingRequestValidityRangeStudentData from "../../../../models/financing-request/validity-range/FinancingRequestValidityRangeStudentData";
import TrTableHeadMetadata from "../../../../models/shared/TrTableHeadMetadata";
import {
  Typography,
  Paper,
  makeStyles,
  TableRow,
  Tooltip
} from "@material-ui/core";
import {
  hrDate,
  nameof,
  hrMonthYear,
  isSchoolWithdrawalMonth
} from "../../../../utilities/utilities";
import Month from "../../../../models/school-year/Month";
import TrTableBodyData from "../../../../models/shared/TrTableBodyData";
import TrSelect from "../../../shared/controls/TrSelect";
import TrSelectData from "../../../../models/shared/TrSelectData";
import TrTable from "../../../shared/controls/TrTable";
import ValidityRangeReason from "../../../../models/financing-request/validity-range/ValidityRangeReason";
import SelectItem from "../../../../models/shared/SelectItem";
import useChangeTracker from "../../../../hooks/change-tracker/useChangeTracker";
import { getValidationConfigurations } from "../../../../models/financing-request/validity-range/FinancingRequestValidityRangeValidation";
import TrackerName from "../../../../models/financing-request/validity-range/FinancingRequestValidityRangeTrackerName";
import useSnackbar from "../../../../hooks/snackbar/useSnackbar";
import TrSnackbar from "../../../shared/controls/TrSnackbar";
import clsx from "clsx";

interface Props extends StepHandler {
  financingRequest: FinancingRequestValidityRange;
  selectChange: (id: number) => any;
  validityRangeReasons: SelectItem<ValidityRangeReason>[];
  forEdit: boolean;
  hasChanged: boolean;
  studentDataAlreadySaved: (studentId: number) => boolean;
}

export default function ValidityRangePickerStep({
  financingRequest,
  selectChange,
  validityRangeReasons,
  forEdit,
  hasChanged,
  studentDataAlreadySaved,
  buttons,
  onPrevious,
  onNext,
  goToPreviousStep,
  goToNextStep
}: Props) {
  const classes = useStyles({});

  useEffect(() => {
    console.log(financingRequest);

    return updateTrackerDebounceEffect([
      [TrackerName.ValidityRange, financingRequest.studentsData]
    ]);
  }, [financingRequest]);

  const {
    isOpened: isSnackbarOpened,
    open: openSnackbar,
    message: snackbarMessage,
    close: closeSnackbar,
    variant: snackbarVariant
  } = useSnackbar();

  const {
    validationResult,
    updateTrackerDebounceEffect,
    showErrors
  } = useChangeTracker(getValidationConfigurations(hasChanged), false);

  const schoolWithdrawal = (
    studentData: FinancingRequestValidityRangeStudentData
  ) => {
    if (
      studentData.student.schoolWithdrawalDate != null &&
      studentData.months != null &&
      studentData.months.length > 0 &&
      studentData.student.schoolWithdrawalDate <=
        studentData.months[studentData.months.length - 1].date &&
      studentData.student.schoolWithdrawalDate >= studentData.months[0].date
    ) {
      return true;
    }
    return false;
  };

  const getMonthItems = (
    studentData: FinancingRequestValidityRangeStudentData
  ) => {
    return {
      valueProp: "date",
      textProp: "name",
      textFunct: (m: Month) =>
        m.name.charAt(0).toUpperCase() +
        m.name.slice(1) +
        " - " +
        new Date(m.date).getFullYear(),
      data: studentData.months,
      emptyOption: !schoolWithdrawal(studentData)
    } as TrSelectData<Month>;
  };

  const getValidityRangeReasonSelectData = () => {
    const selectTypeData: TrSelectData<SelectItem<ValidityRangeReason>> = {
      valueProp: "value",
      textProp: "text",
      data: validityRangeReasons.filter(
        vr => vr.value != ValidityRangeReason.PersonalTransportChange
      ),
      emptyOption: true
    };

    return selectTypeData;
  };

  const getValidityRangeReasonText = (v: ValidityRangeReason) => {
    var reason = validityRangeReasons.find(vr => vr.value == v);
    if (reason) {
      return reason.text;
    }
    return "";
  };

  const tableHeadMetadata: TrTableHeadMetadata<
    FinancingRequestValidityRangeStudentData
  >[] = [
    {
      id: "studentName",
      label: "Učenik",
      dataFunc: (d: FinancingRequestValidityRangeStudentData) => (
        <>
          <Typography variant="body2">
            {d.student.lastName + " " + d.student.firstName}
          </Typography>
          <Typography variant="body2">{hrDate(d.student.birthDate)}</Typography>
        </>
      )
    },
    {
      id: "oib",
      label: "OIB",
      dataFunc: (d: FinancingRequestValidityRangeStudentData) => (
        <>
          <Typography variant="body2">{d.student.oib}</Typography>
        </>
      )
    },
    {
      id: "schoolClass",
      label: "Razred",
      dataFunc: (d: FinancingRequestValidityRangeStudentData) => (
        <>
          <Typography variant="body2">{d.student.programName}</Typography>
          <Typography variant="body2">
            {d.student.schoolClass + " - " + d.student.sectionMark}
          </Typography>
        </>
      )
    },
    {
      id: "vrstaOdjeljenja",
      label: "Vrsta odjeljenja",
      dataFunc: (d: FinancingRequestValidityRangeStudentData) => (
        <Typography variant="body2">{d.student.sectionTypeName}</Typography>
      )
    },
    {
      id: "monthsColumn",
      label: "Završni mjesec važenja troškova",
      dataFunc: (d: FinancingRequestValidityRangeStudentData) =>
        !forEdit ? (
          <Typography variant="body1">
            {d.validTo ? hrMonthYear(d.validTo) : ""}
          </Typography>
        ) : d.validTo &&
          (d.validityRangeReason ==
            ValidityRangeReason.PersonalTransportChange ||
            studentDataAlreadySaved(d.student.id)) ? (
          <Typography variant="body1">{hrMonthYear(d.validTo)}</Typography>
        ) : (
          <TrSelect
            label={""}
            value={d.validTo}
            name={nameof<typeof d>("validTo")}
            fullWidth={true}
            selectData={getMonthItems(d)}
            onChange={selectChange(d.student.id)}
          />
        )
    },
    {
      id: "validityRangeReasonColumn",
      label: "Razlog prestanka",
      dataFunc: (d: FinancingRequestValidityRangeStudentData) =>
        isSchoolWithdrawalMonth(d) ? (
          <Typography variant="body1">
            {d.student.schoolWithdrawalReason}
          </Typography>
        ) : !forEdit ||
          d.validityRangeReason ==
            ValidityRangeReason.PersonalTransportChange ||
          studentDataAlreadySaved(d.student.id) ? (
          <Typography variant="body1" className={classes.reasonsColumn}>
            {d.validityRangeReason
              ? getValidityRangeReasonText(d.validityRangeReason)
              : ""}
          </Typography>
        ) : (
          <TrSelect
            className={classes.reasonsColumn}
            label={""}
            value={d.validityRangeReason}
            name={nameof<typeof d>("validityRangeReason")}
            fullWidth={true}
            selectData={getValidityRangeReasonSelectData()}
            onChange={selectChange(d.student.id)}
          />
        )
    }
  ];

  let studentsDataSort = [...financingRequest.studentsData];

  const tableBodyData: TrTableBodyData = {
    idDataName: "id",
    data:
      financingRequest.studentsData.length > 0
        ? !forEdit
          ? studentsDataSort.sort(function(a, b) {
              if (a.validTo == null) return 1;
              else if (b.validTo == null) return -1;
              else return a.id - b.id;
            })
          : financingRequest.studentsData
        : new Array<FinancingRequestValidityRangeStudentData>()
  };

  const handleBodyRowCreated = (tableRow: ReactElement, rowData: any) => {
    return (
      <TableRow
        key={tableRow.key}
        {...tableRow.props}
        className={clsx(
          !validationResult.isArrayItemValid(
            TrackerName.ValidityRange,
            financingRequest.studentsData
              .findIndex(sd => sd.id == rowData.id)
              .toString()
          ) && classes.error
        )}
      />
    );
  };

  const handlePreviousStep = () => {
    if (goToPreviousStep != null) {
      goToPreviousStep();
    }
  };

  const handleNextStep = () => {
    if (forEdit && !validationResult.isValid(false)) {
      showErrors();

      const validationMessages = validationResult
        .getTrackerMessages(TrackerName.ValidityRange, false)
        .filter(m => m.validationType === "ValidityRange");

      let message = "";
      if (validationMessages.length > 0) {
        message = validationMessages[0].message;
      }
      openSnackbar(message, "error");
    } else {
      goToNextStep && goToNextStep();
    }
  };

  onPrevious && onPrevious(handlePreviousStep);
  onNext && onNext(handleNextStep);

  return (
    <>
      <Paper>
        {financingRequest != null && financingRequest.studentsData != null ? (
          <TrTable
            headMetadata={tableHeadMetadata}
            bodyData={tableBodyData}
            enableSelect={false}
            onBodyRowCreated={handleBodyRowCreated}
          />
        ) : (
          <></>
        )}
      </Paper>
      {buttons}
      <TrSnackbar
        variant={snackbarVariant}
        isOpened={isSnackbarOpened}
        message={snackbarMessage}
        onClose={closeSnackbar}
      />
    </>
  );
}

const useStyles = makeStyles(theme => ({
  reasonsColumn: {
    minWidth: "250px",
    whiteSpace: "normal"
  },
  passengersColumn: {
    paddingLeft: "15px"
  },
  error: {
    "& td": { color: theme.palette.error.main }
  }
}));
